import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import styles from './Nav.module.scss'
import { GoGear } from 'react-icons/go'
import { BiTask } from 'react-icons/bi'
import { IoCalendarOutline } from 'react-icons/io5'
import { MdGTranslate, MdInsights, MdOutlineReceiptLong } from 'react-icons/md'
import { CgNotes } from 'react-icons/cg'
import { LuBanknote, LuBookMinus, LuClipboardPen, LuWallet } from 'react-icons/lu'
import SubNavMenu from '../SubNavMenu/SubNavMenu'
import { RootState } from '../../redux/store'
import { connect } from 'react-redux'
import { AuthUser } from '../../domains/Login/types/users'
import { FaRegFileAlt } from 'react-icons/fa'
import {
  canViewModule,
  getDefaultAnalyticsRoute,
  getVisibleAnalyticsMenuItems,
  getVisibleNoteMenuItems,
  getVisiblePurchaseMenuItems,
  getVisibleSettingsMenuItems,
  getVisibleUtilityBillsMenuItems,
} from '../../utils/moduleAccess'
import { useI18n } from '../../i18n/I18nProvider'

interface NavProps {
  user?: AuthUser
}

const PURCHASE_SUBMENU_TABS = new Set(['daily', 'big', 'products'])

const Nav = (props: NavProps) => {
  const { user } = props
  const { t } = useI18n()
  const location = useLocation()
  const visibleNotesMenu = getVisibleNoteMenuItems(user)
  const visiblePurchasesMenu = getVisiblePurchaseMenuItems(user)
  const visibleUtilityBillsMenu = getVisibleUtilityBillsMenuItems(user)
  const visibleSettingsMenu = getVisibleSettingsMenuItems(user)
  const visibleAnalyticsMenu = getVisibleAnalyticsMenuItems(user)
  const canViewIncome = canViewModule(user, 'income')
  const purchaseTab =
    new URLSearchParams(location.search).get('tab') || 'daily'
  const incomeTab = new URLSearchParams(location.search).get('tab')
  const isPurchasesPath = location.pathname === '/purchases'
  const isIncomePath = location.pathname === '/income'
  const isAnalyticsPath =
    (isPurchasesPath && purchaseTab === 'analytics') ||
    (isIncomePath && incomeTab === 'analytics')
  const analyticsRoute =
    getDefaultAnalyticsRoute(user) || '/purchases?tab=analytics'

  const getPurchaseNavClass = (isActive: boolean) =>
    isActive ? styles.activeNavLink : styles.navLink

  return (
    <div className={styles.root}>
      {visibleNotesMenu.length > 0 && (
        <div>
          <SubNavMenu
            title="Notes"
            menuItems={visibleNotesMenu}
            basePath="/notes"
          >
            <NavLink
              title={t('Notes')}
              className={(naveData) =>
                naveData.isActive ? styles.activeNavLink : styles.navLink
              }
              to={'/notes'}
            >
              <CgNotes size={22} />
            </NavLink>
          </SubNavMenu>
        </div>
      )}

      {canViewModule(user, 'objectives') && (
        <div>
          <NavLink
            title={t('Objectives')}
            className={(naveData) =>
              naveData.isActive ? styles.activeNavLink : styles.navLink
            }
            to={'/objectives'}
          >
            <BiTask size={22} />
          </NavLink>
        </div>
      )}
      {canViewModule(user, 'events') && (
        <div>
          <NavLink
            title={t('Events')}
            className={(naveData) =>
              naveData.isActive ? styles.activeNavLink : styles.navLink
            }
            to={'/events'}
          >
            <IoCalendarOutline size={21} />
          </NavLink>
        </div>
      )}
      {canViewModule(user, 'translation') && (
        <div>
          <NavLink
            title={t('Translation')}
            className={(naveData) =>
              naveData.isActive ? styles.activeNavLink : styles.navLink
            }
            to={'/translation'}
          >
            <MdGTranslate size={20} />
          </NavLink>
        </div>
      )}
      {canViewModule(user, 'whiteboards') && (
        <div>
          <NavLink
            title={t('Whiteboards')}
            className={(naveData) =>
              naveData.isActive ? styles.activeNavLink : styles.navLink
            }
            to={'/whiteboards'}
          >
            <LuClipboardPen size={22} />
          </NavLink>
        </div>
      )}
      {canViewModule(user, 'files') && (
        <div>
          <NavLink
            title={t('Files')}
            className={(naveData) =>
              naveData.isActive ? styles.activeNavLink : styles.navLink
            }
            to={'/files'}
          >
            <FaRegFileAlt size={22} />
          </NavLink>
        </div>
      )}
      {canViewModule(user, 'scenarios') && (
        <div>
          <NavLink
            title={t('Scenarios')}
            className={(naveData) =>
              naveData.isActive ? styles.activeNavLink : styles.navLink
            }
            to={'/scenarios'}
          >
            <LuBookMinus size={22} />
          </NavLink>
        </div>
      )}

      {visiblePurchasesMenu.length > 0 && (
        <div>
          <SubNavMenu
            title="Purchases"
            menuItems={visiblePurchasesMenu}
            basePath="/purchases"
          >
            <NavLink
              title={t('Purchases')}
              className={() =>
                getPurchaseNavClass(
                  isPurchasesPath && PURCHASE_SUBMENU_TABS.has(purchaseTab),
                )
              }
              to={'/purchases?tab=daily'}
            >
              <LuWallet size={23} />
            </NavLink>
          </SubNavMenu>
        </div>
      )}
      {visibleUtilityBillsMenu.length > 0 && (
        <div>
          <SubNavMenu
            title="Utility bills"
            menuItems={visibleUtilityBillsMenu}
            basePath="/purchases"
          >
            <NavLink
              title={t('Utility bills')}
              className={() =>
                getPurchaseNavClass(
                  isPurchasesPath && purchaseTab === 'utilities',
                )
              }
              to={'/purchases?tab=utilities&utility=electricity'}
            >
              <MdOutlineReceiptLong size={22} />
            </NavLink>
          </SubNavMenu>
        </div>
      )}
      {canViewIncome && (
        <div>
          <NavLink
            title={t('Income')}
            className={() =>
              getPurchaseNavClass(isIncomePath && incomeTab !== 'analytics')
            }
            to={'/income'}
          >
            <LuBanknote size={22} />
          </NavLink>
        </div>
      )}
      {visibleAnalyticsMenu.length > 0 && (
        <div>
          <SubNavMenu
            title="Analytics"
            menuItems={visibleAnalyticsMenu}
          >
            <NavLink
              title={t('Analytics')}
              className={() => getPurchaseNavClass(isAnalyticsPath)}
              to={analyticsRoute}
            >
              <MdInsights size={22} />
            </NavLink>
          </SubNavMenu>
        </div>
      )}
      {visibleSettingsMenu.length > 0 && (
        <div>
          <SubNavMenu
            title="Settings"
            menuItems={visibleSettingsMenu}
            basePath="/settings"
          >
            <NavLink
              title={t('Settings')}
              className={(naveData) =>
                naveData.isActive ? styles.activeNavLink : styles.navLink
              }
              to={'/settings/users'}
            >
              <GoGear size={22} />
            </NavLink>
          </SubNavMenu>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
})

export default connect(mapStateToProps, {})(Nav)
